import { Directive, ElementRef, HostListener, inject } from '@angular/core';
import { MapSkin, SettingsService } from '../services/account/settings.service';
import { VEHICLE_IMAGE_FALLBACK_URL } from '../services/core/http.service';

/**
 * Swaps a vehicle `<img>` over to the local default car icon when the S3 image
 * fails to load (bucket offline, missing icon for the car, no network).
 *
 * The skin is taken from the failing URL when possible, so images that pin a
 * skin explicitly (e.g. always-dark dropdown icons) fall back to the matching
 * local asset instead of the user's current map skin.
 */
@Directive({
  selector: 'img[appVehicleImageFallback]',
  standalone: true
})
export class VehicleImageFallbackDirective {

  private readonly host = inject(ElementRef<HTMLImageElement>);
  private readonly settingsService = inject(SettingsService);

  @HostListener('error')
  public onError(): void {
    const img: HTMLImageElement = this.host.nativeElement;
    const fallback = VEHICLE_IMAGE_FALLBACK_URL.replace('$skin', this.resolveSkin(img.getAttribute('src')));

    // The fallback itself failed - stop here, otherwise we'd loop on the error event.
    if (img.getAttribute('src') === fallback) {
      return;
    }

    img.src = fallback;
  }

  private resolveSkin(failedSrc: string | null): string {
    const skinInSrc = failedSrc?.match(/\/icons\/(light|dark)\//)?.[1];
    return skinInSrc ?? this.settingsService.getMap()?.Skin ?? MapSkin.Light;
  }
}
